/**
 * /auth/callback — OAuth landing page (ADR-0021).
 *
 * Supabase's PKCE flow returns here with a `code` query param. The browser
 * SDK exchanges it for a session automatically (because we set
 * `detectSessionInUrl: true` in `supabase.ts`). We then:
 *
 *   1. Wait for the SDK to finish the exchange
 *   2. Call userStore.refresh() to pull profile + effective tier
 *   3. Navigate to the post-sign-in target (priority: ?next= → localStorage
 *      breadcrumb → /studio?tab=themes default)
 *
 * On any failure we still navigate home — the user can re-try sign-in.
 *
 * Sprint 28+: localStorage breadcrumb fallback added because Supabase's
 * `redirectTo` is silently ignored when the host isn't in the project-level
 * "Redirect URLs" allowlist. Without the breadcrumb, users get stranded at
 * the Site URL (jubit.ai) after Google OAuth.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { isAuthConfigured, supabase } from '../lib/supabase.ts';
import { useUserStore } from '../state/userStore.ts';
import { exchangeJubitSsoToken } from '../auth/jubitSsoExchange.ts';

const DEFAULT_NEXT = '/studio?tab=themes';
const RETURN_TO_KEY = 'jubuddy:auth-return-to';

function isSafeInternalPath(raw: string | null): raw is string {
  return !!raw && raw.startsWith('/') && !raw.startsWith('//');
}

function safeNext(raw: string | null): string {
  if (isSafeInternalPath(raw)) return raw;
  try {
    const stashed = window.localStorage.getItem(RETURN_TO_KEY);
    if (isSafeInternalPath(stashed)) return stashed;
  } catch {
    /* private mode / cookies disabled — fall through */
  }
  return DEFAULT_NEXT;
}

function clearReturnBreadcrumb(): void {
  try {
    window.localStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* best effort */
  }
}

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refresh = useUserStore((s) => s.refresh);
  const [error, setError] = useState<string | null>(null);
  // The SSO token is single-use and consumed atomically by the hub, so React's
  // double-invoked effects would burn it and turn a good sign-in into
  // "already used token". Redeem at most once per mount.
  const redeemedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!isAuthConfigured) {
        setError('Auth is not configured for this environment.');
        return;
      }
      try {
        // Cross-domain return from jubit.ai: the hub sends us here with a one-time
        // exchange token rather than a PKCE code. Without this branch the token was
        // ignored and the visitor landed back on jubuddy still signed out, having
        // just authenticated successfully on jubit.ai.
        const ssoToken = params.get('sso_token');
        if (ssoToken) {
          if (redeemedRef.current) return;
          redeemedRef.current = true;

          const result = await exchangeJubitSsoToken(ssoToken);
          if (cancelled) return;
          if (!result.ok) {
            setError(result.error ?? 'Sign-in failed');
            return;
          }
          await refresh();
          if (cancelled) return;
          const ssoDest = safeNext(params.get('next'));
          clearReturnBreadcrumb();
          navigate(ssoDest, { replace: true });
          return;
        }

        // Wait for Supabase SDK to finish the URL→session exchange.
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          setError(sessionError.message);
          return;
        }
        if (!data.session) {
          // Sometimes the SDK needs a tick to populate; retry once.
          await new Promise((r) => setTimeout(r, 250));
        }
        if (cancelled) return;
        await refresh();
        if (cancelled) return;
        const dest = safeNext(params.get('next'));
        clearReturnBreadcrumb();
        navigate(dest, { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Sign-in failed');
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate, params, refresh]);

  return (
    <div className="flex min-h-screen flex-col bg-jtk-surface-1 text-jtk-primary">
      <header className="flex items-center justify-between border-b border-jtk-subtle px-4 py-3">
        <Link to="/" className="text-xs font-bold tracking-tight text-jub-tile hover:brightness-110">
          ← Jubuddy
        </Link>
        <span className="text-xs uppercase tracking-wider text-jtk-whisper">Sign in</span>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-12">
        {error ? (
          <section
            role="alert"
            className="w-full max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center"
          >
            <h1 className="text-lg font-bold text-red-200">Sign-in failed</h1>
            <p className="mt-2 text-sm text-red-100">{error}</p>
            <Link to="/studio?tab=themes" className="mt-4 inline-block text-xs underline">
              Back to Studio
            </Link>
          </section>
        ) : (
          <div className="flex items-center gap-3 text-sm text-jtk-secondary" role="status" aria-live="polite">
            <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-jub-tile" aria-hidden />
            Completing sign-in…
          </div>
        )}
      </main>
    </div>
  );
}
